import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faMapMarkerAlt,
  faVideo,
  faInfoCircle,
  faUserFriends,
  faClock,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import LoadingSkeleton from "../../componentes/LoadingSkeleton";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { fetchEventDetails } from "../../api/events";
import { formatDate } from "../../utils/dateFormatter";
import "./ckeditor-content.css";
import "../../styles/circuit-background.css";

const backgroundIcons = [faCalendarAlt, faMapMarkerAlt, faVideo, faInfoCircle];

const EventDetails = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();

  const {
    data: event,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["eventDetails", slug, i18n.language],
    queryFn: () => fetchEventDetails(slug, i18n.language),
  });

  const getStatusClass = (status) => {
    switch (status) {
      case "upcoming":
        return "bg-blue-500";
      case "ongoing":
        return "bg-green-500";
      case "completed":
        return "bg-gray-500";
      default:
        return "bg-gray-300";
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("events.loading")}
          description=""
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("events.title"), link: "/events" },
            { label: t("events.loading"), link: null },
          ]}
        />
        <FullscreenSpinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("events.error")}
          description={t("events.error_description")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("events.title"), link: "/events" },
            { label: t("events.error"), link: null },
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-500">{error}</p>
        </div>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("events.not_found")}
          description={t("events.not_found_description")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("events.title"), link: "/events" },
            { label: t("events.not_found"), link: null },
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-gray-500">{t("events.not_found_message")}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader />
      <div className="max-xl:pl-4 bg-gradient-to-r from-midnigt to-shake mt-[72px] pt-8 sm:pt-8 xl:pt-8 px-dynamic min-h-[14rem] md:min-h-[15rem] relative overflow-hidden">
        {/* Animated background elements */}
        {backgroundIcons.length > 0 && (
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  opacity: 0.1,
                }}
              >
                <FontAwesomeIcon
                  icon={backgroundIcons[i % backgroundIcons.length]}
                  className="text-white text-4xl"
                />
              </div>
            ))}
          </div>
        )}

        {/* Content with animations */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-white/80 text-sm md:mb-8 mb-6 max-xl:mt-8"
          >
            <Link to="/" className="hover:text-white transition-colors">
              {t("header.home")}
            </Link>
            <span>/</span>
            <Link to="/events" className="hover:text-white transition-colors">
              {t("events.title")}
            </Link>
            <span>/</span>
            <span className="break-words">
              {event.title || event.translations?.title || t("events.title")}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-h-5 font-bold text-white tracking-tight font-catamaran break-words max-w-[90%] md:max-w-[80%] mb-4 md:mb-6"
            >
              {event.title || event.translations?.title || "Event Detail"}
            </motion.h1>

            {/* Status Alert Box */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex w-fit rounded-lg border border-white/20 overflow-hidden shadow-lg"
            >
              <div
                className={`w-12 md:w-16 ${getStatusClass(
                  event.status
                )} flex items-center justify-center`}
              >
                <FontAwesomeIcon
                  icon={event.is_virtual ? faVideo : faMapMarkerAlt}
                  className="text-white text-lg md:text-2xl"
                />
              </div>
              <div className="w-fit text-white px-3 md:px-4 py-2 md:py-3">
                <p className="hidden xl:block text-body font-nunito-sans">
                  {t("events.status_label")}
                </p>
                <p className="text-base md:text-lg font-medium">
                  {t(`events.status.${event.status}`)}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="xl:col-span-2">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >


                {/* Article Header */}
                <div className="p-8 pb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                      <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2 text-primary" />
                      {formatDate(event.start_date, i18n.language)}
                    </span>
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white ${getStatusClass(event.status)}`}>
                      <FontAwesomeIcon icon={event.is_virtual ? faVideo : faMapMarkerAlt} className="w-3 h-3" />
                      {t(`events.status.${event.status}`)}
                    </div>
                  </div>
                </div>

                {/* Image Section */}
                {event.image && (
                  <div className="px-6 lg:px-8 mb-8">
                    <div className="aspect-video overflow-hidden rounded-xl shadow-lg">
                      <img
                        src={event.image}
                        alt={event.translations?.title || "Event image"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Article Content */}
                <div className="px-6 lg:px-8 pb-8">
                  {/* Language fallback notice */}
                  {event.translations?.is_fallback && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3"
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-blue-500 mt-1 flex-shrink-0"
                      />
                      <p className="text-blue-800 text-sm font-nunito-sans">
                        {t("events.language_fallback_notice", {
                          requested: i18n.language,
                          actual: event.translations.actual_language,
                        })}
                      </p>
                    </motion.div>
                  )}

                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        event.description ||
                        event.translations?.description ||
                        t("events.no_description") ||
                        "No Description",
                    }}
                    className="rich-content"
                  />

                  {/* Event Agenda */}
                  {event.translations?.agenda && (
                    <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                      <h3 className="flex items-center gap-2 text-gray-800 font-bold font-catamaran mb-4">
                        <FontAwesomeIcon icon={faClock} className="text-primary" />
                        {t("events.agenda")}
                      </h3>
                      <div
                        className="prose prose-sm max-w-none prose-headings:font-catamaran prose-p:font-nunito-sans"
                        dangerouslySetInnerHTML={{
                          __html: event.translations.agenda,
                        }}
                      />
                    </div>
                  )}

                  {/* Organizer Info */}
                  {event.organizer && (
                    <div className="mt-8 p-6 bg-primary/5 rounded-xl border border-primary/10">
                      <h3 className="flex items-center gap-2 text-gray-800 font-bold font-catamaran mb-3">
                        <FontAwesomeIcon icon={faUserFriends} className="text-primary" />
                        {t("events.organizer")}
                      </h3>
                      <p className="text-gray-700 font-nunito-sans">{event.organizer}</p>
                    </div>
                  )}
                </div>
              </motion.article>
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg p-6 sticky top-8"
              >


                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-primary w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                        {t("events.start_date")}
                      </h4>
                      <p className="font-nunito-sans text-sm text-gray-600">
                        {formatDate(event.start_date, i18n.language)}
                      </p>
                    </div>
                  </div>

                  {event.end_date && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faCalendarAlt} className="text-primary w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                          {t("events.end_date")}
                        </h4>
                        <p className="font-nunito-sans text-sm text-gray-600">
                          {formatDate(event.end_date, i18n.language)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={event.is_virtual ? faVideo : faMapMarkerAlt} className="text-primary w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                        {t("events.location")}
                      </h4>
                      <p className="font-nunito-sans text-sm text-gray-600">
                        {event.is_virtual ? t("events.virtual_event") : event.location}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
                  {event.registration_url && event.status !== "completed" && (
                    <a
                      href={event.registration_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl transition-colors font-nunito-sans text-sm font-medium"
                    >
                      <FontAwesomeIcon icon={faUserFriends} className="mr-2" />
                      {t("events.register_now")}
                    </a>
                  )}

                  {event.url && (
                    <a
                      href={event.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center px-4 py-3 bg-gray-100 hover:bg-primary hover:text-white rounded-xl transition-colors font-nunito-sans text-sm font-medium"
                    >
                      <FontAwesomeIcon icon={faLink} className="mr-2" />
                      {t("events.visit_event_website")}
                    </a>
                  )}


                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventDetails;
